import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Button } from 'antd'
import { verifyResetPasswordLink, setPasswordFromLink } from '../api'
import { setSession } from '@/lib/session'
import { buildAuthSession } from '../session'
import type { VerifyResetPasswordResponse } from '../types'
import AuthLinkLayout from '../components/AuthLinkLayout'
import SetPasswordForm from '../components/SetPasswordForm'
import { authLog } from '../debug'

export default function ResetPasswordPage() {
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invalidLink, setInvalidLink] = useState(false)
  const [email, setEmail] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [signInRequired, setSignInRequired] = useState(false)

  useEffect(() => {
    if (!id) {
      authLog('Reset password page missing id')
      setInvalidLink(true)
      setLoading(false)
      return
    }
    authLog('Reset password link verification started', { id })
    verifyResetPasswordLink(id)
      .then((res: VerifyResetPasswordResponse) => {
        authLog('Reset password link verification response', { id, res })
        if (res.message) {
          setInvalidLink(true)
          setError(`Link has been ${res.message}.`)
        } else {
          setEmail(res.email ?? null)
        }
      })
      .catch((e) => {
        authLog('Reset password link verification failed', e)
        setInvalidLink(true)
      })
      .finally(() => setLoading(false))
  }, [id])

  const onFinish = async (values: { password: string; confirmPassword: string }) => {
    if (!id || !email) return
    setError(null)
    setSubmitting(true)
    authLog('Reset password submit', { id, email })
    try {
      const res = await setPasswordFromLink(email, values.password, id)
      authLog('Reset password response received', { id, email, res })
      if (res?.message === 'SignInRequired') {
        // Authenticator-app users get no tokens here; they sign in with the new password and a code.
        setSignInRequired(true)
        setDone(true)
        setTimeout(() => {
          window.location.href = '/login'
        }, 2500)
      } else if (res?.idToken && res?.refreshToken) {
        const nextSession = await buildAuthSession(
          email,
          res.idToken,
          res.refreshToken,
          res.expiry ?? 3600
        )
        setSession(nextSession)
        setDone(true)
        setTimeout(() => {
          window.location.href = '/'
        }, 1500)
      } else {
        setError('Failed to set password. Please try again.')
      }
    } catch (e) {
      authLog('Reset password flow threw error', e)
      setError(e instanceof Error ? e.message : 'Failed to set password.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <AuthLinkLayout
        successMessage={
          signInRequired
            ? 'Password updated. Sign in with your new password and your authenticator code. Redirecting...'
            : 'Password updated. Redirecting...'
        }
      />
    )
  }

  return (
    <AuthLinkLayout
      loading={loading}
      invalidLink={invalidLink}
      invalidTitle="Invalid or expired link"
      invalidDescription="This reset link is no longer valid. Request a new one from the sign-in page."
      invalidError={error}
      invalidActions={
        <Link to="/login">
          <Button type="primary">Go to sign in</Button>
        </Link>
      }
    >
      <SetPasswordForm
        email={email}
        passwordLabel="New password"
        submitLabel="Update password"
        onSubmit={onFinish}
        loading={submitting}
        error={error}
      />
    </AuthLinkLayout>
  )
}
